// login.js — authentication UI and flows.
//
// Every visitor is signed in anonymously as soon as the app loads, so their
// data syncs to the cloud from the first task. "Sign up" upgrades (links) that
// anonymous account to Google or email/password, keeping the same user id, so
// nothing needs merging. "Log in" to an account that already exists switches
// to it; the local mirror of the anonymous data is merged by the app's login
// callback (fishbowl.js), exactly as before.

import { auth } from "./firebase.js";
import { deleteAllUserData, loadAllCompleted } from "./db.js";
import {
  onAuthStateChanged,
  signInAnonymously,
  GoogleAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  linkWithPopup,
  linkWithRedirect,
  linkWithCredential,
  signInWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  deleteUser,
  signOut
} from "firebase/auth";

const googleProvider = new GoogleAuthProvider();

// Popups are unreliable on iOS Safari and inside installed (standalone) web
// apps; use a full-page redirect there.
function shouldUseRedirect() {
  const ua = navigator.userAgent || '';
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
  return ios || standalone;
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
const modalHTML = `
  <div id="auth-modal" class="auth-modal hidden" role="dialog" aria-modal="true" aria-labelledby="auth-title">
    <div class="auth-overlay"></div>
    <div class="auth-modal-content">
      <h3 id="auth-title">Log in</h3>
      <p class="auth-sub">Keep your tasks and history safe across devices.</p>

      <button id="google-btn" type="button" class="auth-google">Continue with Google</button>
      <div class="divider">or</div>

      <form id="auth-form" novalidate>
        <input id="email-input" type="email" name="email" placeholder="Email" autocomplete="email" required />
        <input id="pass-input" type="password" name="password" placeholder="Password" autocomplete="current-password" required minlength="6" />
        <button id="email-btn" type="submit" class="auth-primary">Log in</button>
      </form>

      <p id="auth-error" class="auth-error" role="alert" hidden></p>
      <p id="auth-info" class="auth-info" hidden></p>

      <p class="auth-switch">
        <a href="#" id="auth-forgot">Forgot password?</a>
        <span class="auth-dot">·</span>
        <a href="#" id="auth-toggle">Create an account</a>
      </p>
      <button id="auth-cancel" type="button" class="cancel-btn">Cancel</button>
    </div>
  </div>
`;
document.body.insertAdjacentHTML('beforeend', modalHTML);

const accountHTML = `
  <div id="account-modal" class="auth-modal hidden" role="dialog" aria-modal="true" aria-labelledby="account-title">
    <div class="auth-overlay"></div>
    <div class="auth-modal-content account-panel">
      <h3 id="account-title">Your account</h3>
      <p class="auth-sub" id="account-email"></p>

      <div id="account-main">
        <button id="account-logout" type="button" class="auth-google">Log out</button>
        <div class="divider">your data</div>
        <button id="account-export-json" type="button" class="auth-google">Download as JSON</button>
        <button id="account-export-csv" type="button" class="auth-google">Download as CSV</button>
        <p class="account-hint">Everything: open tasks, history with times, tags, streak.</p>
        <div class="divider"></div>
        <button id="account-delete-start" type="button" class="auth-danger-link">Delete my account…</button>
      </div>

      <div id="account-delete" hidden>
        <p class="account-warn"><strong>This permanently deletes your account and all your data</strong> — tasks, history, tags and streak. It cannot be undone. Download your data first if you want to keep it.</p>
        <label class="account-check"><input type="checkbox" id="account-delete-confirm" /> I understand this cannot be undone</label>
        <input id="account-delete-pass" type="password" placeholder="Confirm your password" autocomplete="current-password" hidden />
        <button id="account-delete-go" type="button" class="auth-danger" disabled>Delete permanently</button>
        <button id="account-delete-back" type="button" class="cancel-btn">Back</button>
      </div>

      <p id="account-error" class="auth-error" role="alert" hidden></p>
      <p id="account-info" class="auth-info" hidden></p>
      <button id="account-close" type="button" class="cancel-btn">Close</button>
    </div>
  </div>
`;
document.body.insertAdjacentHTML('beforeend', accountHTML);

const style = document.createElement('style');
style.textContent = `
  .auth-modal.hidden { display: none; }
  .auth-modal { position: fixed; top:0; left:0; width:100%; height:100%; z-index: 10000; }
  .auth-overlay { position:absolute; inset:0; background: rgba(0,0,0,0.5); }
  .auth-modal-content {
    position: relative; background:#fff; margin:10vh auto; padding:1.5rem;
    width:90%; max-width:340px; border-radius:8px; box-shadow:0 4px 12px rgba(0,0,0,0.1);
    z-index: 10001; text-align:center; font-family: 'Inter', sans-serif;
  }
  .auth-modal-content h3 { margin: 0 0 0.25rem; font-size:1.2rem; }
  .auth-sub { margin: 0 0 1rem; font-size: 0.85rem; color: #666; }
  .auth-modal-content input {
    width:100%; padding:0.6rem; margin:0.4rem 0; font-size:1rem;
    border:1px solid #ccc; border-radius:4px; box-sizing: border-box;
  }
  .auth-modal-content button { width:100%; padding:0.8rem; margin:0.4rem 0; font-size:1rem; border:none; border-radius:4px; cursor:pointer; }
  .auth-google { background: #fff; color: #333; border: 1px solid #ccc !important; }
  .auth-google:hover { background: #f5f5f5; }
  .auth-primary { background: #0077cc; color: #fff; font-weight: 600; }
  .auth-primary:hover { background: #005fa3; }
  .auth-primary[disabled], .auth-google[disabled] { opacity: 0.6; cursor: wait; }
  .auth-modal-content .divider { margin: 0.6rem 0; color: #888; }
  .auth-error { color: #b00020; font-size: 0.9rem; margin: 0.5rem 0 0; }
  .auth-info { color: #2e7d32; font-size: 0.9rem; margin: 0.5rem 0 0; }
  .auth-switch { font-size: 0.85rem; margin: 0.75rem 0 0; }
  .auth-switch a { color: #0077cc; text-decoration: none; }
  .auth-switch a:hover { text-decoration: underline; }
  .auth-dot { color: #999; margin: 0 0.4em; }
  .cancel-btn { background: transparent; color: #555; text-decoration: underline; }
  .account-panel { text-align: left; }
  .account-panel h3, .account-panel .auth-sub, .account-panel .divider { text-align: center; }
  .account-panel #account-email { word-break: break-all; }
  .account-hint { font-size: 0.8rem; color: #666; margin: 0.2rem 0 0.6rem; text-align: center; }
  .auth-danger-link { background: none !important; color: #b00020 !important; text-decoration: underline; padding: 0.4rem !important; }
  .auth-danger { background: #b00020; color: #fff; font-weight: 600; }
  .auth-danger[disabled] { opacity: 0.45; cursor: not-allowed; }
  .account-warn { font-size: 0.9rem; line-height: 1.5; color: #333; }
  .account-check { display: flex; gap: 0.5em; align-items: flex-start; font-size: 0.9rem; margin: 0.6rem 0; cursor: pointer; }
  .account-check input { width: auto; margin: 0.2em 0 0; }
  @media (prefers-color-scheme: dark) {
    .account-warn { color: #ddd; }
    .account-hint { color: #aaa; }
    .auth-modal-content { background: #1e1e1e; color: #eee; }
    .auth-sub { color: #aaa; }
    .auth-modal-content input { background: #2a2a2a; color: #eee; border-color: #555; }
    .auth-google { background: #2c2c2c; color: #eee; border-color: #555 !important; }
    .auth-google:hover { background: #3a3a3a; }
    .cancel-btn { color: #bbb; }
  }
`;
document.head.appendChild(style);

// Human-readable messages for the Firebase error codes users actually hit.
function friendlyError(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-email': return 'That email address does not look right.';
    case 'auth/missing-password': return 'Please enter your password.';
    case 'auth/weak-password': return 'Please use a password of at least 6 characters.';
    case 'auth/email-already-in-use': return 'An account with this email already exists. Try "Log in" instead.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials': return 'Wrong email or password.';
    case 'auth/too-many-requests': return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/popup-blocked': return 'Your browser blocked the sign-in window. Please allow popups and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request': return '';
    case 'auth/network-request-failed': return 'No connection. Please check your internet and try again.';
    case 'auth/operation-not-allowed':
    case 'auth/admin-restricted-operation': return 'This sign-in method is not enabled yet. Please try another method or contact support.';
    case 'auth/unauthorized-domain': return 'Sign-in is not allowed from this address.';
    case 'auth/credential-already-in-use':
    case 'auth/account-exists-with-different-credential': return 'This Google account is already registered. Logging you in…';
    default: return 'Something went wrong (' + (code || err?.message || 'unknown') + ').';
  }
}

// ---------------------------------------------------------------------------
// Auth flows
// ---------------------------------------------------------------------------

// Upgrade the current anonymous account with Google, or sign in with Google.
// If the Google account already belongs to another user, switch to that user;
// the app's login callback merges this device's local data into it.
async function googleSignIn() {
  const user = auth.currentUser;
  if (shouldUseRedirect()) {
    if (user?.isAnonymous) return linkWithRedirect(user, googleProvider);
    return signInWithRedirect(auth, googleProvider);
  }
  if (user?.isAnonymous) {
    try {
      return await linkWithPopup(user, googleProvider);
    } catch (err) {
      if (err.code === 'auth/credential-already-in-use' || err.code === 'auth/account-exists-with-different-credential') {
        const cred = GoogleAuthProvider.credentialFromError(err);
        if (cred) return signInWithCredential(auth, cred);
      }
      throw err;
    }
  }
  return signInWithPopup(auth, googleProvider);
}

async function emailCreateAccount(email, pw) {
  const user = auth.currentUser;
  if (user?.isAnonymous) {
    try {
      return await linkWithCredential(user, EmailAuthProvider.credential(email, pw));
    } catch (err) {
      // With "email enumeration protection" enabled, Firebase refuses to attach an
      // email to an anonymous account ("verify the new email before changing email").
      // Fall back to a brand-new account; the app's login callback merges this
      // device's local data into it, exactly as when logging in to an existing account.
      if (err.code === 'auth/operation-not-allowed') {
        console.warn('Linking email to anonymous account refused; creating a new account instead.');
        return createUserWithEmailAndPassword(auth, email, pw);
      }
      throw err;
    }
  }
  return createUserWithEmailAndPassword(auth, email, pw);
}

async function emailLogIn(email, pw) {
  return signInWithEmailAndPassword(auth, email, pw);
}

// Sign out of the real account and return to a fresh anonymous one.
// Local data is cleared so the next person at this browser starts empty.
export async function logOutAndReset() {
  const keepOnboarded = localStorage.getItem('fishbowl_onboarded');
  localStorage.clear();
  if (keepOnboarded) localStorage.setItem('fishbowl_onboarded', keepOnboarded);
  await signOut(auth);
  // onAuthStateChanged(null) below signs in anonymously again.
}

// ---------------------------------------------------------------------------
// Data export helpers
// ---------------------------------------------------------------------------
function downloadFile(filename, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
}
function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}
export function buildCsv(data) {
  const rows = [['status', 'text', 'tag', 'duration_minutes', 'duration_seconds', 'created_on', 'started_on', 'completed_on', 'skips']];
  (data.tasks || []).forEach(t => rows.push(['open', t.text, t.tag, '', '', t.createdOn, '', '', (t.skips || []).length]));
  (data.manualQueue || []).forEach(t => rows.push(['queued', t.text, t.tag, '', '', t.createdOn, '', '', (t.skips || []).length]));
  (data.completed || []).forEach(c => {
    const sec = (typeof c.durationSec === 'number') ? c.durationSec : null;
    rows.push(['completed', c.text, c.tag, sec === null ? '' : (sec / 60).toFixed(2), sec === null ? '' : sec, c.createdOn, c.startedOn, c.completedOn, '']);
  });
  return '\ufeff' + rows.map(r => r.map(csvCell).join(',')).join('\r\n');
}
function stamp() { return new Date().toISOString().slice(0, 10); }

// ---------------------------------------------------------------------------
// Account panel (signed-in users)
// ---------------------------------------------------------------------------
function setupAccountPanel(getExportData) {
  const modal = document.getElementById('account-modal');
  const q = id => document.getElementById(id);
  const emailEl = q('account-email'), main = q('account-main'), del = q('account-delete');
  const errorEl = q('account-error'), infoEl = q('account-info');
  const passEl = q('account-delete-pass'), confirmEl = q('account-delete-confirm'), goBtn = q('account-delete-go');
  const showError = m => { errorEl.textContent = m; errorEl.hidden = !m; };
  const showInfo = m => { infoEl.textContent = m; infoEl.hidden = !m; };

  function open() {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) return;
    emailEl.textContent = user.email || '';
    main.hidden = false; del.hidden = true;
    confirmEl.checked = false; passEl.value = ''; goBtn.disabled = true;
    showError(''); showInfo('');
    modal.classList.remove('hidden');
  }
  function close() { modal.classList.add('hidden'); }

  modal.querySelector('.auth-overlay').addEventListener('click', close);
  q('account-close').addEventListener('click', close);
  q('account-logout').addEventListener('click', async () => { close(); await logOutAndReset(); });

  // The app only holds recent history in memory; exports fetch everything.
  async function fullExportData() {
    const data = getExportData();
    try { data.completed = await loadAllCompleted(); } catch (e) { console.warn('Full history unavailable, exporting what is loaded:', e); }
    return data;
  }
  q('account-export-json').addEventListener('click', async () => {
    showInfo('Preparing…');
    const data = await fullExportData();
    const payload = { app: 'Fishbowl', exportedAt: new Date().toISOString(), account: { email: auth.currentUser?.email || null }, ...data };
    downloadFile(`fishbowl-export-${stamp()}.json`, 'application/json', JSON.stringify(payload, null, 2));
    showInfo('JSON download started.');
  });
  q('account-export-csv').addEventListener('click', async () => {
    showInfo('Preparing…');
    downloadFile(`fishbowl-export-${stamp()}.csv`, 'text/csv;charset=utf-8', buildCsv(await fullExportData()));
    showInfo('CSV download started.');
  });

  q('account-delete-start').addEventListener('click', () => {
    const user = auth.currentUser;
    const usesPassword = (user?.providerData || []).some(p => p.providerId === 'password');
    passEl.hidden = !usesPassword;
    main.hidden = true; del.hidden = false; showError(''); showInfo('');
  });
  q('account-delete-back').addEventListener('click', () => { main.hidden = false; del.hidden = true; showError(''); });
  confirmEl.addEventListener('change', () => { goBtn.disabled = !confirmEl.checked; });

  goBtn.addEventListener('click', async () => {
    const user = auth.currentUser;
    if (!user || user.isAnonymous || !confirmEl.checked) return;
    goBtn.disabled = true; showError(''); showInfo('Deleting…');
    try {
      // Firebase requires a recent sign-in for destructive actions.
      const usesPassword = (user.providerData || []).some(p => p.providerId === 'password');
      if (usesPassword) {
        if (!passEl.value) throw Object.assign(new Error('missing'), { code: 'auth/missing-password' });
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, passEl.value));
      } else {
        await reauthenticateWithPopup(user, googleProvider);
      }
      await deleteAllUserData();
      const keepOnboarded = localStorage.getItem('fishbowl_onboarded');
      localStorage.clear();
      if (keepOnboarded) localStorage.setItem('fishbowl_onboarded', keepOnboarded);
      await deleteUser(user); // signs out; onAuthStateChanged(null) starts a fresh anonymous session
      close();
      alert('Your account and all your data have been deleted.');
      location.reload();
    } catch (err) {
      console.warn('Account deletion failed:', err);
      showInfo('');
      showError(err.code === 'auth/requires-recent-login'
        ? 'Please log out, log in again, and retry the deletion.'
        : friendlyError(err));
      goBtn.disabled = false;
    }
  });

  return { open };
}

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------
// options.getExportData: () => { tasks, completed, tags, manualQueue, streakInfo }
export function setupLoginButton(btnId, onUserLogin = () => {}, options = {}) {
  const accountPanel = setupAccountPanel(options.getExportData || (() => ({})));
  const loginBtn = document.getElementById(btnId);
  const modal = document.getElementById("auth-modal");
  const overlay = modal.querySelector(".auth-overlay");
  const title = document.getElementById("auth-title");
  const googleBtn = document.getElementById("google-btn");
  const form = document.getElementById("auth-form");
  const emailBtn = document.getElementById("email-btn");
  const emailInput = document.getElementById("email-input");
  const passInput = document.getElementById("pass-input");
  const errorEl = document.getElementById("auth-error");
  const infoEl = document.getElementById("auth-info");
  const forgotLink = document.getElementById("auth-forgot");
  const toggleLink = document.getElementById("auth-toggle");
  const cancelBtn = document.getElementById("auth-cancel");

  let mode = 'login'; // or 'signup'
  let authReady = false;

  function setMode(m) {
    mode = m;
    const signup = mode === 'signup';
    title.textContent = signup ? 'Create account' : 'Log in';
    emailBtn.textContent = signup ? 'Create account' : 'Log in';
    toggleLink.textContent = signup ? 'I already have an account' : 'Create an account';
    passInput.autocomplete = signup ? 'new-password' : 'current-password';
    forgotLink.style.display = signup ? 'none' : '';
    showError('');
    showInfo('');
  }
  function showError(msg) { errorEl.textContent = msg; errorEl.hidden = !msg; }
  function showInfo(msg) { infoEl.textContent = msg; infoEl.hidden = !msg; }
  function busy(on) { emailBtn.disabled = on; googleBtn.disabled = on; }

  function showModal() {
    setMode('login');
    modal.classList.remove('hidden');
    emailInput.value = "";
    passInput.value = "";
    if (!authReady) showInfo('Connecting…');
    setTimeout(() => { if (!modal.contains(document.activeElement)) emailInput.focus(); }, 50);
  }
  function hideModal() { modal.classList.add('hidden'); busy(false); }

  // The button works from the first paint; auth state only changes its label.
  loginBtn.onclick = showModal;
  overlay.addEventListener('click', hideModal);
  cancelBtn.addEventListener('click', hideModal);
  toggleLink.addEventListener('click', e => { e.preventDefault(); setMode(mode === 'login' ? 'signup' : 'login'); });

  googleBtn.addEventListener('click', async () => {
    showError(''); busy(true);
    try {
      await googleSignIn();
      hideModal();
      // Linking keeps the same user, so onAuthStateChanged may not fire: refresh the UI explicitly.
      if (auth.currentUser && !auth.currentUser.isAnonymous) applyUser(auth.currentUser);
    } catch (err) {
      console.warn('Google sign-in failed:', err);
      showError(friendlyError(err));
    } finally { busy(false); }
  });

  form.addEventListener('submit', async e => {
    e.preventDefault();
    const email = emailInput.value.trim();
    const pw = passInput.value;
    if (!email || !pw) return showError('Please enter both email and password.');
    showError(''); busy(true);
    try {
      if (mode === 'signup') await emailCreateAccount(email, pw);
      else await emailLogIn(email, pw);
      hideModal();
      if (auth.currentUser && !auth.currentUser.isAnonymous) applyUser(auth.currentUser);
    } catch (err) {
      console.warn('Email auth failed:', err);
      showError(friendlyError(err));
    } finally { busy(false); }
  });

  forgotLink.addEventListener('click', async e => {
    e.preventDefault();
    const email = emailInput.value.trim();
    if (!email) return showError('Enter your email above first, then click "Forgot password?".');
    showError('');
    try {
      await sendPasswordResetEmail(auth, email);
      showInfo(`Password reset email sent to ${email}. Check your inbox (and spam folder).`);
    } catch (err) {
      showError(friendlyError(err));
    }
  });

  let lastAppliedUid = null;
  function applyUser(user) {
    if (user && !user.isAnonymous) {
      const email = user.email || '';
      const prefix = email.split('@')[0];
      loginBtn.innerHTML = `<strong>Account</strong><br/><span class="email-full">${email}</span><span class="email-short">${prefix}</span>`;
      loginBtn.title = email + ' — account, data export, log out';
      loginBtn.onclick = () => accountPanel.open();
    } else {
      loginBtn.innerHTML = `Sign up / Log in`;
      loginBtn.removeAttribute('title');
      loginBtn.onclick = showModal;
    }
    if (user && user.uid !== lastAppliedUid) {
      lastAppliedUid = user.uid;
      onUserLogin(user);
    } else if (user && !user.isAnonymous) {
      // Same uid, upgraded from anonymous: no data to merge, just the UI above.
    }
  }

  // Finish a redirect-based Google sign-in, if one is in flight.
  getRedirectResult(auth).catch(async err => {
    if (err.code === 'auth/credential-already-in-use' || err.code === 'auth/account-exists-with-different-credential') {
      const cred = GoogleAuthProvider.credentialFromError(err);
      if (cred) { try { await signInWithCredential(auth, cred); } catch (e) { console.warn(e); } }
    } else if (err.code) {
      console.warn('Redirect sign-in failed:', err);
    }
  });

  onAuthStateChanged(auth, user => {
    authReady = true;
    showInfo('');
    if (!user) {
      lastAppliedUid = null;
      signInAnonymously(auth).catch(err => console.warn('Anonymous sign-in failed:', err));
      return;
    }
    applyUser(user);
  });
}
